import { supabase } from "@/integrations/supabase/client";
import type { Role } from "@/lib/access";
import { isAdminLevel } from "@/lib/access";

/**
 * Government funding eligibility codes are Front Desk, Admin and Owner only. They live in
 * their own table; other roles get no rows back, so the code stays blank and
 * the field is hidden on screen for them.
 */
export const canSeeFundingCode = (role: Role) => isAdminLevel(role) || role === "front_desk";

export const FUNDING_CODE_ACCESS_MESSAGE = "Funding codes are only visible to Front Desk, Admins and Owners.";

/** Never submit a hidden field, even when it survives in a restored draft. */
export function fundingCodePayload<T extends { funding_code?: string | null }>(value: T, role: Role): Omit<T, "funding_code"> & { funding_code?: string | null } {
  const { funding_code, ...rest } = value;
  return canSeeFundingCode(role) ? { ...rest, funding_code } : rest;
}

export async function attachFundingCodes<T extends { id: string; funding_code?: string | null }>(rows: T[]): Promise<T[]> {
  if (!rows.length) return rows;
  const { data, error } = await supabase
    .from("child_funding_codes")
    .select("child_id, funding_code");
  if (error) throw error;
  if (!data?.length) return rows.map((r) => ({ ...r, funding_code: "" }));
  const map = new Map(data.map((d) => [d.child_id, d.funding_code]));
  return rows.map((r) => ({ ...r, funding_code: map.get(r.id) ?? "" }));
}

/** Explicitly save or clear a code; database permissions enforce access. */
export async function saveFundingCode(childId: string, nurseryId: string, code: string) {
  const value = code.trim();
  if (value) {
    const { error } = await supabase
      .from("child_funding_codes")
      .upsert({ child_id: childId, nursery_id: nurseryId, funding_code: value, updated_at: new Date().toISOString() });
    if (error) throw error;
  } else {
    const { error } = await supabase.from("child_funding_codes").delete().eq("child_id", childId).eq("nursery_id", nurseryId);
    if (error) throw error;
  }
}
